import { DiagnosisTimeSlot } from '@/models/diagnosis';
import { ECategoryType, EDeprecatedCategoryType } from '@/services/doctor/DiagnosisCategoryService';
import { DiagnosisPriceType, DoctorBadgeType, MedicineType, TelemedicineTargetType } from '@/types/bff/gql-schema';
import { Duty } from '@/types/common';

import { DoctorVaccinations } from '@/entities/vaccination/index';
import { EDiagnosisContactType } from './diagnosis';

export interface DoctorDetailPageQueryParam {
  doctorId?: string;
  categoryId?: string;
  type?: ECategoryType;
  telemedicineTargetType?: TelemedicineTargetType;
  treatmentMedicineId?: string;
  treatmentMedicineUnitId?: string;
  treatmentMedicineStrengthId?: string;
  contactType?: EDiagnosisContactType;
  priceType?: DiagnosisPriceType;
  entry?: EDeprecatedCategoryType;
  isNeedReview?: boolean;
  vaccinationTypeId?: string;
  vaccinationUnitId?: string;
  vaccinationFreePolicyId?: string;
}
export interface DoctorDetailAnswer {
  readonly answerPid: string;
  readonly content: string;
  readonly doctorId: string;
  readonly doctorName: string;
  readonly doctorProfileImageUrl: string;
  readonly isBlockedByUser: boolean;
}

export interface Department {
  id: string;
  name: string;
  isProfessional: boolean;
}
export enum DayOfWeek {
  Mon = 1,
  Tue = 2,
  Wed = 3,
  Thu = 4,
  Fri = 5,
  Sat = 6,
  Sun = 7,
  Hol = 8,
}

export interface Doctor {
  id: string;
  name: string;
  departments: Array<Department>;
  profileImageUrl: string;
  averageRating: number;
  reviewCount: number;
  isNonF2FBookable: boolean;
  isNonF2FNonBenefitBookable: boolean;
  isF2FBookable: boolean;
  doctorDuties: {
    f2f: Array<Duty>;
    nonF2f: Array<Duty>;
  };
  todayDuty?: Duty;
  career: Array<Career>;
  simpleIntroduce?: string;
  introduce?: string;
  detailImageUrls: Array<string>;
  medicineType: MedicineType;
  readyForVideoCall: boolean;
}

export enum CareerType {
  Current = 'CURRENT',
  Past = 'PAST',
  Graduation = 'GRADUATION',
  Completion = 'COMPLETION',
}

export interface Career {
  type: CareerType;
  content: string;
  id: string;
  isRepresentative: boolean;
  sequence: number;
}
export interface HospitalBrand {
  brandName: string;
  inHospitalDelivery: boolean;
}
export interface Hospital {
  name: string;
  latitude?: number;
  longitude?: number;
  jibunAddress?: string;
  roadAddress: string;
  detailAddress?: string;
  tel?: string;
  brand?: HospitalBrand;
}

interface Medicine {
  id: string;
  treatmentMedicineName: string;
  unitDisplayName: string;
  unitTypeDisplayName: string;
  originalPrice: number;
  priceOnUsingPoint: number;
  priceType: DiagnosisPriceType;
  strengthUnit?: string;
}

export interface ExtendedMedicine extends Medicine {
  treatmentId: string;
  treatmentName: string;
}

export interface NonBenefitTreatment {
  id: string;
  name: string;
  medicines: Array<Medicine>;
}
export interface DoctorTreatment {
  treatmentId: string;
  treatmentName: string;
}

export interface VaccineProduct {
  vaccineProductId: string;
  vaccinationTypeUnitId: string;
  unit: number;
  price: number;
  product: {
    name: string;
  };
}

export interface VaccinationType {
  vaccinationTypeId: string;
  name: string;
  products: Array<VaccineProduct>;
}

export interface DoctorVaccination {
  treatmentId: string;
  treatmentName: string;
  supportedFreePolicyIds: Array<string>;
  vaccinationTypes: Array<VaccinationType>;
}

export interface DoctorBadge {
  title: string;
  description: string;
  iconUrl: string;
  type: DoctorBadgeType;
}

export interface Notice {
  title: string;
  content: string;
}
export interface ReviewTagGroup {
  count: number;
  name: string;
  tagId: string;
}

export interface ReviewRatingStatistic {
  diagnosisType: DiagnosisTypeVo;
  diagnosisTypeId: string;
  name: string;
  averageRating: string;
  count: number;
}
export enum EBookingAvailabilityVo {
  TodayBookable = 'TODAY_BOOKABLE',
  TomorrowBookable = 'TOMORROW_BOOKABLE',
  UpcomingBookable = 'UPCOMING_BOOKABLE',
  NotBookable = 'NOT_BOOKABLE',
  Unknown = 'UNKNOWN',
}

export interface DoctorBookingInfo {
  bookingAvailability: EBookingAvailabilityVo;
  doctorId: string;
  todayBookable: boolean;
  todayCutoffTime: string;
  todayKickoffTime: string;
  tomorrowBookable: boolean;
  tomorrowCutoffTime: string;
  tomorrowKickoffTime: string;
}
export interface FamilyDoctorInfo {
  createdAt: number;
  doctorId: string;
}
export interface DoctorDetail {
  doctor: Doctor;
  hospital: Hospital;
  nonBenefitTreatments: Array<NonBenefitTreatment>;
  reviewTagGroups: Array<ReviewTagGroup>;
  doctorDiagnosisTypeReviewRatingStatistics: Array<ReviewRatingStatistic>;
  treatments: Array<DoctorTreatment>;
  badges: Array<DoctorBadge>;
  notice: Notice;
  diagnosisBookingSchedules: Array<NonFaceToFaceDiagnosisBookingSchedulesVo>;
  bookingInfo: DoctorBookingInfo;
  familyDoctorInfo: FamilyDoctorInfo | null;
  // DoctorVaccinations 모델로 관리
  vaccinations?: DoctorVaccinations;
}

export interface NonFaceToFaceDiagnosisBookingSchedulesVo {
  displayName: string;
  schedules: {
    remainingCount: number;
    startTimeSlot: DiagnosisTimeSlot;
    endTimeSlot: DiagnosisTimeSlot;
    bookingStatus: BookingScheduleTypeVo;
  }[];
  status: BookingScheduleStatus;
  targetDate: string;
}

export interface ReviewCountByCategory {
  count: number;
  diagnosisType: DiagnosisTypeVo;
  diagnosisTypeId: string;
  name: string;
}

export enum DiagnosisTypeVo {
  Department = 'DEPARTMENT',
  TREATMENT = 'TREATMENT',
}

export enum BookingScheduleStatus {
  Bookable = 'BOOKABLE',
  FullyBooked = 'FULLY_BOOKED',
  Holiday = 'HOLIDAY',
  Unknown = 'UNKNOWN',
}

export enum BookingScheduleTypeVo {
  Bookable = 'BOOKABLE',
  Disabled = 'DISABLED',
  FullyBooked = 'FULLY_BOOKED',
  Unknown = 'UNKNOWN',
}
export enum DoctorBookingStatus {
  TodayBookable = 'TodayBookable',
  TomorrowBookable = 'TomorrowBookable',
  BookingUnavailable = 'BookingUnavailable',
}
